/**
 * CapPanel — Ridge / Barge cap metadata strip rendered beneath a
 * ComponentLineRow. Iter220 — extracted from `AppraisalComponentsTab.js`
 * for shared use; behaviour unchanged.
 *
 * Props:
 *   api               — axios instance (drives dropdown CRUD)
 *   line              — current component line {cap_type, cap_length_m,
 *                                                cap_count, length}
 *   onUpdate(patch)   — patch handler (updates the line in parent state)
 *   dropdownCategory  — 'ridge_cap_types' | 'barge_cap_types'
 *   label             — Display label ("Ridge caps" | "Barge caps")
 *   colour            — 'purple' (Ridge) | 'violet' (Barge)
 */
import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';

export default function CapPanel({ api, line, onUpdate, dropdownCategory, label, colour = 'purple', roofSubtypeCtx = '', tileProfileCtx = '', appraisalIdCtx = '' }) {
  const [types, setTypes] = useState([]);     // full meta-tagged list
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMm, setNewMm] = useState('');
  const [error, setError] = useState('');
  // Iter235 — sticky-per-appraisal "Show all" toggle. Stored in
  // localStorage so the override survives a refresh on THIS appraisal
  // but doesn't bleed into other appraisals.
  const showAllKey = appraisalIdCtx
    ? `${dropdownCategory}_show_all:${appraisalIdCtx}`
    : '';
  const [showAll, setShowAll] = useState(() => {
    if (!showAllKey) return false;
    try { return window.localStorage.getItem(showAllKey) === '1'; }
    catch { return false; }
  });

  const palette = colour === 'violet'
    ? { text: 'text-violet-700', bg: 'bg-violet-50', ring: 'ring-violet-100', btn: 'bg-violet-50 hover:bg-violet-100 ring-violet-200' }
    : { text: 'text-purple-700', bg: 'bg-purple-50', ring: 'ring-purple-100', btn: 'bg-purple-50 hover:bg-purple-100 ring-purple-200' };

  useEffect(() => {
    let cancel = false;
    // Iter235 — switched to the meta-tagged endpoint so the dropdown
    // can be filtered by roof type + tile profile. Each entry now
    // carries { label, roof_kinds, profile_keys, source }.
    api.get(`/dropdowns/${dropdownCategory}/with-meta`).then(r => {
      if (cancel) return;
      const data = r.data;
      // Normalize: backend returns objects, but fall back to strings
      // (for any future endpoint shape changes / mocked test data).
      const arr = Array.isArray(data) ? data : (data?.options || []);
      setTypes(arr.map(item => typeof item === 'string'
        ? { label: item, roof_kinds: ['concrete', 'clay'], profile_keys: [], source: 'legacy' }
        : item));
    }).catch(() => {});
    return () => { cancel = true; };
  }, [api, dropdownCategory]);

  // Iter235 — apply roof_subtype + tile_profile filter unless "Show
  // all" is enabled. Iron / Tin-Corrugate roofs see no caps at all
  // because they don't use precast caps — we still render the empty
  // dropdown so the operator can flip Show all if they need an
  // unusual cap, e.g. an Iron-tile hybrid job.
  const ROOF_KIND_FROM_SUBTYPE = (s) => {
    const t = String(s || '').toLowerCase();
    if (t.includes('tin tile') || t.includes('tin-tile') || t.includes('decra') || t.includes('colortile')) return 'tin';
    if (t.includes('clay')) return 'clay';
    if (t.includes('concrete') || t.includes('tile')) return 'concrete';
    return '';   // Iron / Corrugate / unknown → no kind match
  };
  const PROFILE_KEY_FROM_LABEL = (p) => {
    const t = String(p || '').toLowerCase();
    if (t.includes('monier')) return 'monier';
    if (t.includes('decra') || t.includes('colortile')) return 'decramastic';
    return '';
  };
  const targetKind = ROOF_KIND_FROM_SUBTYPE(roofSubtypeCtx);
  const targetProfile = PROFILE_KEY_FROM_LABEL(tileProfileCtx);

  const filteredTypes = (() => {
    if (showAll) return types;
    if (!targetKind) return types;        // can't filter — show all by default
    // Keep entries whose roof_kinds include the target kind.
    let pool = types.filter(t => (t.roof_kinds || []).includes(targetKind));
    // Narrow further by profile when one is set (option 1: filter
    // narrow to just that profile). Entries with NO profile_keys are
    // generic and always kept.
    if (targetProfile) {
      pool = pool.filter(t =>
        !t.profile_keys || t.profile_keys.length === 0 ||
        t.profile_keys.includes(targetProfile)
      );
    }
    return pool;
  })();

  const onToggleShowAll = (next) => {
    setShowAll(next);
    if (showAllKey) {
      try {
        if (next) window.localStorage.setItem(showAllKey, '1');
        else window.localStorage.removeItem(showAllKey);
      } catch { /* localStorage disabled — silently ignore */ }
    }
  };

  const mmFromLabel = (text) => {
    const m = String(text || '').match(/(\d{2,4})\s*mm/i);
    return m ? parseInt(m[1], 10) : 0;
  };

  const onPickType = (text) => {
    const mm = mmFromLabel(text);
    const cap = mm > 0 ? parseFloat((mm / 1000).toFixed(3)) : Number(line.cap_length_m) || 0;
    const patch = { cap_type: text, cap_length_m: cap };
    if (cap > 0 && Number(line.cap_count) > 0) patch.length = parseFloat((cap * Number(line.cap_count)).toFixed(3));
    onUpdate(patch);
  };

  const onCapLengthChange = (cl) => {
    const patch = { cap_length_m: cl };
    if (mmFromLabel(line.cap_type) !== Math.round(cl * 1000)) patch.cap_type = '';
    if (cl > 0 && Number(line.cap_count) > 0) patch.length = parseFloat((cl * Number(line.cap_count)).toFixed(3));
    onUpdate(patch);
  };

  const saveNewType = async () => {
    const cl = Number(line.cap_length_m);
    if (!cl || cl <= 0) return;
    const mm = Math.round(cl * 1000);
    setNewName('');
    setNewMm(String(mm));
    setError('');
    setShowAdd(true);
  };

  const submitNewType = async () => {
    const name = (newName || '').trim();
    const mm = parseInt(newMm, 10);
    if (!name) { setError('Please enter a name.'); return; }
    if (!mm || mm <= 0) { setError('Cap length must be a positive number in mm.'); return; }
    const newLabel = `${name} (${mm}mm)`;
    setSaving(true);
    setError('');
    try {
      const r = await api.post(`/dropdowns/${dropdownCategory}/add`, { value: newLabel });
      const data = r.data;
      // Iter235 — the /add endpoint still returns the legacy string
      // array. Re-fetch the meta-tagged listing so the new entry gets
      // its heuristic classification immediately.
      const opts = Array.isArray(data) ? data : (data?.options || []);
      try {
        const r2 = await api.get(`/dropdowns/${dropdownCategory}/with-meta`);
        const arr = Array.isArray(r2.data) ? r2.data : [];
        setTypes(arr.map(item => typeof item === 'string'
          ? { label: item, roof_kinds: ['concrete', 'clay'], profile_keys: [], source: 'legacy' }
          : item));
      } catch {
        // Fall back to wrapping the bare list if the meta call fails.
        setTypes(opts.map(s => ({ label: s, roof_kinds: ['concrete', 'clay'], profile_keys: [], source: 'legacy' })));
      }
      onPickType(newLabel);
      setShowAdd(false);
      setNewName('');
      setNewMm('');
    } catch (e) {
      setError(`Could not add new ${label.toLowerCase()} type. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const addBrandNewType = () => {
    setNewName('');
    setNewMm('');
    setError('');
    setShowAdd(true);
  };

  const customLength = Number(line.cap_length_m) > 0 && !line.cap_type;

  return (
    <div className="col-span-12 -mt-1">
      <details className="text-xs" open={Number(line.cap_count) > 0 || Number(line.cap_length_m) > 0}>
        <summary className="cursor-pointer text-slate-500 hover:text-slate-700 select-none flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{label}</span>
          {Number(line.cap_count) > 0 && Number(line.cap_length_m) > 0 && (
            <span className={`text-[10px] font-mono-num ${palette.text} ${palette.bg} ring-1 ${palette.ring} px-1.5 py-0.5 rounded`}>
              {Number(line.cap_count)} × {Number(line.cap_length_m)}m = {(Number(line.cap_count) * Number(line.cap_length_m)).toFixed(2)}m total
            </span>
          )}
        </summary>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Cap type</label>
            <select data-testid={`${dropdownCategory}-type`} value={line.cap_type || ''} onChange={e => onPickType(e.target.value)} className="text-sm px-2 py-1 border border-[#E2E2DF] rounded bg-white">
              <option value="">— Select / custom —</option>
              {filteredTypes.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
              {/* Iter235 — if the line's current cap_type was filtered
                  out (operator narrowed roof type after picking it),
                  still show it so the value stays editable. */}
              {line.cap_type && !filteredTypes.find(t => t.label === line.cap_type) && (
                <option value={line.cap_type}>{line.cap_type} (filtered out)</option>
              )}
            </select>
            <button type="button" data-testid={`${dropdownCategory}-add`} onClick={addBrandNewType} disabled={saving} title={`Add a new ${label.toLowerCase()} type to the dropdown`} className={`inline-flex items-center gap-1 text-[10px] font-bold ${palette.text} ${palette.btn} ring-1 px-1.5 py-1 rounded transition-colors disabled:opacity-50`}>
              <Plus size={11} strokeWidth={2.5} /> Add new
            </button>
            {/* Iter235 — Show-all toggle, only meaningful when a filter
                is in play (i.e. some entries WOULD be hidden if showAll
                were off). Stay mounted while showAll is true so the
                operator can switch the filter back on without devtools.
                Sticky per appraisal via localStorage. */}
            {!!targetKind && (showAll || filteredTypes.length < types.length) && (() => {
              // Count what would be hidden if showAll were off — used
              // for the "(N hidden)" badge so it stays accurate even
              // when the toggle is currently engaged.
              let wouldFilter = types.filter(t => (t.roof_kinds || []).includes(targetKind));
              if (targetProfile) {
                wouldFilter = wouldFilter.filter(t =>
                  !t.profile_keys || t.profile_keys.length === 0 ||
                  t.profile_keys.includes(targetProfile)
                );
              }
              const hiddenCount = types.length - wouldFilter.length;
              return (
                <label
                  className="inline-flex items-center gap-1 text-[10px] text-slate-500 ml-1 select-none cursor-pointer"
                  title={`${hiddenCount} cap${hiddenCount === 1 ? '' : 's'} hidden when off — only ${targetKind}${targetProfile ? ' / ' + targetProfile : ''} caps shown`}
                  data-testid={`${dropdownCategory}-show-all-toggle`}
                >
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(e) => onToggleShowAll(e.target.checked)}
                    className="h-3 w-3"
                  />
                  Show all
                  {hiddenCount > 0 && (
                    <span className="font-mono-num text-slate-400">({hiddenCount} hidden)</span>
                  )}
                </label>
              );
            })()}
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Caps</label>
            <input data-testid="cap-count" type="number" step="1" value={line.cap_count ?? 0} onChange={e => {
              const c = parseFloat(e.target.value) || 0;
              const patch = { cap_count: c };
              if (c > 0 && Number(line.cap_length_m) > 0) patch.length = parseFloat((c * Number(line.cap_length_m)).toFixed(3));
              onUpdate(patch);
            }} className="w-20 text-sm px-2 py-1 border border-[#E2E2DF] rounded font-mono-num" />
          </div>
          <span className="text-slate-400 font-bold">×</span>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Cap length</label>
            <input data-testid="cap-length" type="number" step="0.001" value={line.cap_length_m ?? 0} onChange={e => onCapLengthChange(parseFloat(e.target.value) || 0)} className="w-24 text-sm px-2 py-1 border border-[#E2E2DF] rounded font-mono-num" />
            <span className="text-[10px] text-slate-400">m</span>
          </div>
          {customLength && (
            <button type="button" data-testid="cap-save-new" onClick={saveNewType} disabled={saving} className={`inline-flex items-center gap-1 text-[10px] font-bold ${palette.text} ${palette.btn} ring-1 px-2 py-1 rounded transition-colors disabled:opacity-50`}>
              <Plus size={11} strokeWidth={2.5} /> Save new type
            </button>
          )}
          {Number(line.cap_count) > 0 && Number(line.cap_length_m) > 0 && (
            <span className={`text-[10px] font-mono-num ${palette.text} ml-2`}>
              → Length auto-set to <span className="font-bold">{(Number(line.cap_count) * Number(line.cap_length_m)).toFixed(2)}m</span>
            </span>
          )}
        </div>
      </details>
      {showAdd && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setShowAdd(false)} data-testid="cap-add-modal">
          <div className="bg-white rounded-lg shadow-xl ring-1 ring-slate-200 w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <h3 className={`text-sm font-bold ${palette.text} mb-3`}>Add new {label.toLowerCase().replace(/s$/, '')} type</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1">Name</label>
                <input data-testid="cap-add-name" autoFocus type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Tudor" className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1">Cap length (mm)</label>
                <input data-testid="cap-add-mm" type="number" step="1" value={newMm} onChange={e => setNewMm(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submitNewType(); }} placeholder="e.g. 350" className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num" />
              </div>
              {error && <p className="text-xs text-rose-600 font-semibold">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShowAdd(false)} disabled={saving} className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-50">Cancel</button>
              <button type="button" data-testid="cap-add-save" onClick={submitNewType} disabled={saving} className={`text-xs font-bold text-white bg-[#D35400] hover:bg-[#A04000] px-3 py-1.5 rounded-md transition-colors disabled:opacity-50`}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
